import React, { useState, useRef, useEffect } from 'react';
import { PourConfig, CalculationResult, ChatMessage, Language } from '../types';
import { translations } from '../utils/translations';
import { getExpertAnalysis, chatWithExpert } from '../services/geminiService';
import { Sparkles, MessageSquare, User, Loader2, Send, Bot } from 'lucide-react';

interface ExpertAdvisorProps {
  config: PourConfig;
  result: CalculationResult;
  language: Language;
  t: typeof translations['en'];
}

const ExpertAdvisor: React.FC<ExpertAdvisorProps> = ({ config, result, language, t }) => {
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatLoading]);

  // Clear analysis when dimensions change to zero, or language changes
  useEffect(() => {
     if(result.totalVolume === 0) setAnalysis(null);
  }, [result.totalVolume]);

  const handleAnalyze = async () => {
    if (result.totalVolume === 0) return;
    setLoading(true);
    try {
      const advice = await getExpertAnalysis(config, result, language);
      setAnalysis(advice);
    } catch (error) {
      setAnalysis("Unable to connect to the expert system.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    
    const userMsg: ChatMessage = { role: 'user', text: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setChatLoading(true);

    const context = analysis ? `Previous analysis: ${analysis}` : undefined;
    const response = await chatWithExpert(userMsg.text, context, language);
    
    setMessages(prev => [...prev, { role: 'model', text: response }]);
    setChatLoading(false);
  };

  return (
    <div className="space-y-8">
      {/* AI Analysis Section */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-xl backdrop-blur-sm">
               <Sparkles className="w-5 h-5 text-yellow-300" />
            </div>
            <div>
              <h3 className="font-bold text-lg leading-tight">{t.advisor.title}</h3>
              <p className="text-indigo-200 text-xs font-medium">{t.advisor.subtitle}</p>
            </div>
          </div>
          {!analysis && result.totalVolume > 0 && (
            <button
              onClick={handleAnalyze}
              disabled={loading}
              className="px-5 py-2.5 bg-white text-indigo-700 text-sm font-bold rounded-xl hover:bg-indigo-50 shadow-lg disabled:opacity-50 transition-all flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? t.advisor.analyzingBtn : t.advisor.analyzeBtn}
            </button>
          )}
        </div>

        <div className="p-6 md:p-8">
          {loading ? (
             <div className="space-y-4 animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                <div className="h-4 bg-slate-200 rounded w-full"></div>
                <div className="h-4 bg-slate-200 rounded w-5/6"></div>
                <div className="flex items-center gap-2 text-slate-400 text-sm mt-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t.advisor.loading}</span>
                </div>
             </div>
          ) : !analysis ? (
            <div className="text-center py-6 text-slate-500">
               <p className="mb-2">{t.advisor.empty}</p>
               <p className="text-sm">{t.advisor.emptyDesc}</p>
            </div>
          ) : (
            <div className="prose prose-sm prose-slate max-w-none">
              <div className="bg-indigo-50/50 rounded-2xl p-5 border border-indigo-100">
                <React.Fragment>
                  {analysis.split('\n').map((line, i) => (
                    <p key={i} className={`
                      ${line.startsWith('**') ? 'font-bold text-indigo-900 mt-4 mb-2' : 'text-slate-700 mb-1'}
                      ${line.trim().startsWith('-') ? 'ml-4 list-disc list-inside' : ''}
                    `}>
                      {line.replace(/\*\*/g, '')}
                    </p>
                  ))}
                </React.Fragment>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chat Bot */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 flex flex-col h-[500px] overflow-hidden">
        <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <h3 className="font-bold text-slate-700 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-indigo-500" />
            {t.advisor.chatTitle}
          </h3>
          <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{t.advisor.online}</span>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/30">
          {messages.length === 0 && (
             <div className="h-full flex flex-col items-center justify-center text-slate-400 text-sm gap-2">
               <Bot className="w-8 h-8 opacity-20" />
               <p>{t.advisor.chatEmpty}</p>
             </div>
          )}
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'model' && (
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-5 h-5 text-indigo-600" />
                </div>
              )}
              <div className={`max-w-[80%] rounded-2xl px-5 py-3 text-sm leading-relaxed shadow-sm ${
                msg.role === 'user' 
                  ? 'bg-indigo-600 text-white rounded-tr-none' 
                  : 'bg-white text-slate-700 border border-slate-100 rounded-tl-none'
              }`}>
                {msg.text}
              </div>
              {msg.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-slate-500" />
                </div>
              )}
            </div>
          ))}
          {chatLoading && (
            <div className="flex gap-3 justify-start">
               <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-5 h-5 text-indigo-600" />
                </div>
               <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-none px-4 py-3 shadow-sm flex items-center gap-1">
                 <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"></span>
                 <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                 <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
               </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        <div className="p-4 border-t border-slate-100 bg-white">
          <form 
            onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}
            className="relative"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t.advisor.chatPlaceholder}
              className="w-full pl-5 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none text-sm transition-all"
            />
            <button
              type="submit"
              disabled={!input.trim() || chatLoading}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:hover:bg-indigo-600 transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ExpertAdvisor;